import { FC, useMemo } from 'react';
import type { LawName } from '../../../../api/rs-hg/risk/law.types';
import type { ProspectusRestrictionsTimeSeries } from '../../../../api/rs-hg/risk/prospectusRestrictions.types';
import type { RuleTimeSeriesPoint } from '../../../../api/rs-hg/risk/ruleTimeSeries.types';
import { percentageToTwoDecimalPlaces } from '../../../../utilities/numberFormatters';
import RuleTimeSeriesCell from './RuleTimeSeriesCell';

/** Convert the law page's embedded `historical_time_series` into seed points. */
export function lawSeriesToPoints(
  series: unknown,
): RuleTimeSeriesPoint[] | null {
  if (!Array.isArray(series) || series.length === 0) return null;
  const rows = series.filter((row): row is unknown[] => Array.isArray(row));
  if (!rows.length) return null;
  const firstDate = rows[0]?.[0];
  const start =
    typeof firstDate === 'string' && firstDate.toLowerCase() === 'date' ? 1 : 0;
  const points = rows.slice(start).flatMap((row) => {
    const date = row[0];
    if (date == null || date === '') return [];
    return [
      {
        position_date: String(date),
        value: (row[1] as RuleTimeSeriesPoint['value']) ?? null,
        limit: (row[2] as RuleTimeSeriesPoint['limit']) ?? null,
      },
    ];
  });
  return points.length ? points : null;
}

type LawTimeSeriesCellProps = {
  lawName: LawName;
  fundName: string;
  ruleNumber: number;
  disabled?: boolean;
  emptyMessage?: string;
  emptyTooltip?: string;
  initialTimeSeries?: unknown;
};

export const LawTimeSeriesCell: FC<LawTimeSeriesCellProps> = ({
  lawName,
  fundName,
  ruleNumber,
  disabled = false,
  emptyMessage,
  emptyTooltip,
  initialTimeSeries,
}) => {
  const ruleKey = `rule_${ruleNumber}`;
  // No embedded series: leave undefined so the cell fetches the default range.
  const initialPoints = useMemo(
    () => lawSeriesToPoints(initialTimeSeries) ?? undefined,
    [initialTimeSeries],
  );

  return (
    <RuleTimeSeriesCell
      timeSeriesKey="law"
      ruleParams={{ lawName, fundName, ruleKey }}
      chartId={`${lawName}-${fundName}-${ruleKey}`}
      enabled={!disabled && !!fundName && !!lawName}
      emptyMessage={emptyMessage}
      emptyTooltip={emptyTooltip}
      initialPoints={initialPoints}
      formatter={percentageToTwoDecimalPlaces}
    />
  );
};

/** Embedded prospectus cells are a number, a string, or `{ display, value|limit }`. */
function prospectusCell(
  cell: unknown,
  key: 'value' | 'limit',
): { raw: number | string | null; display: string | null } {
  if (typeof cell === 'number') return { raw: cell, display: null };
  if (typeof cell === 'string') return { raw: cell, display: cell };
  if (cell && typeof cell === 'object') {
    const o = cell as Record<string, unknown>;
    const raw = o[key];
    return {
      raw: typeof raw === 'number' || typeof raw === 'string' ? raw : null,
      display:
        typeof o.display === 'string'
          ? o.display
          : typeof raw === 'string'
            ? raw
            : null,
    };
  }
  return { raw: null, display: null };
}

/** Convert the prospectus page's embedded series (header row first) into seed points. */
export function prospectusSeriesToPoints(
  series: ProspectusRestrictionsTimeSeries,
): RuleTimeSeriesPoint[] {
  return series.slice(1).map(([date, value, limit]) => {
    const v = prospectusCell(value, 'value');
    const l = prospectusCell(limit, 'limit');
    return {
      position_date: String(date),
      value: v.raw,
      limit: l.raw,
      value_display: v.display,
      limit_display: l.display,
    };
  });
}

type ProspectusRestrictionsTimeSeriesCellProps = {
  fundName: string;
  ruleIndex: number;
  ruleName: string;
  initialTimeSeries: ProspectusRestrictionsTimeSeries | null;
};

export const ProspectusRestrictionsTimeSeriesCell: FC<
  ProspectusRestrictionsTimeSeriesCellProps
> = ({ fundName, ruleIndex, ruleName, initialTimeSeries }) => {
  // No embedded series: leave undefined so the cell fetches the default range.
  const initialPoints = useMemo(
    () =>
      initialTimeSeries
        ? prospectusSeriesToPoints(initialTimeSeries)
        : undefined,
    [initialTimeSeries],
  );

  return (
    <RuleTimeSeriesCell
      timeSeriesKey="prospectus"
      ruleParams={{ fundName, ruleIndex, ruleName }}
      chartId={`${fundName}-${ruleIndex}`}
      enabled={!!fundName && !!ruleName}
      initialPoints={initialPoints}
    />
  );
};
